
import { Typography } from '@mui/material';

import { useOnlineBookingApi } from 'shell';
import { useEffect, useState } from 'react';
import { AdditionalTextStyle, LinkBoxRoot, LinkErrorStyle, LinkWidget, TitleBox } from './styles';


/**
 * LinkBox component renders a link box with optional additional text and error handling.
 * It handles redirection based on the provided link data and property ID.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.linkData - The data for the link.
 * @param {string} props.linkData.additionalText - Additional text to display below the link.
 * @param {boolean} props.linkData.executeBeforeProxy - Flag to determine if a proxy execution is needed before redirection.
 * @param {string} props.linkData.errorText - Text to display in case of an error.
 * @param {string} props.linkData.text - The main text of the link.
 * @param {string} props.linkData.urlHref - The URL to redirect to from properties.
 * @param {string} props.propertyId - The ID of the property for which the link is being generated.
 * @returns {JSX.Element} The rendered LinkBox component.
 */
const LinkBox = ({ linkData, propertyId }) => {
  const { additionalText, executeBeforeProxy, errorText, text, urlHref: hrefFromProperties } = linkData
  const api = useOnlineBookingApi();

  const [isQueryEnabled, setIsQueryEnabled] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const { isLoading, data, error } = api.links.useLinksQuery(
    propertyId,
    isQueryEnabled
  );

  const hrefFromLinksApi = !isLoading && data?.urlHref

  const redirectToLink = () => {
    if (executeBeforeProxy && !hrefFromLinksApi) {
      setIsQueryEnabled(true);
    }
    if (!executeBeforeProxy) {
      setIsQueryEnabled(false);
    }
    setShouldRedirect(true);
  };

  useEffect(() => {
    if (executeBeforeProxy && hrefFromLinksApi && shouldRedirect) {
      window.location.href = hrefFromLinksApi;
      setIsQueryEnabled(false);
      setShouldRedirect(false);
    }
    else if (!executeBeforeProxy && hrefFromProperties && shouldRedirect) {
      window.open(hrefFromProperties, '_blank');
      setShouldRedirect(false);
    }
  }, [hrefFromLinksApi, isQueryEnabled, shouldRedirect, executeBeforeProxy, hrefFromProperties]);
  if (error) return <LinkBoxRoot direction='column' justifyContent='space-between' alignItems='start'>
    <TitleBox>
      <Typography>{text}</Typography>
    </TitleBox>
    <LinkErrorStyle>{errorText}</LinkErrorStyle>
  </LinkBoxRoot>

  return (
    <LinkBoxRoot direction='column' justifyContent='space-between' alignItems='start'>
      <LinkWidget
        component="button"
        onClick={redirectToLink}>{text}
      </LinkWidget>
      <AdditionalTextStyle>{additionalText}</AdditionalTextStyle>
    </LinkBoxRoot >
  );
}

export default LinkBox;